"use client";
import { TypeComparisonModule } from "@contentful/types";
import { Button } from "@yardzen/ui/components/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@yardzen/ui/components/table";
import { cn } from "@yardzen/ui/lib/utils";
import { Typography } from "@yardzen/ui/components/typography";
import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import { useBuilderPartnerCheckoutHref } from "./builderPartnerCheckoutHref";

function pathOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return (
      new URL(url, "https://yardzen.com").pathname.replace(/\/$/, "") || "/"
    );
  } catch {
    return null;
  }
}

export function ComparisonModule({
  comparisonModule,
  isMultiComparisonModule,
  tabSwitcher,
}: {
  comparisonModule: TypeComparisonModule<"WITHOUT_UNRESOLVABLE_LINKS">;
  isMultiComparisonModule?: boolean;
  tabSwitcher?: ReactNode;
}) {
  const {
    heading,
    mobileHeading,
    subheading,
    comparisonTableHeader: tableHeader,
    comparisonItems: tableRows,
    goodButtonText,
    betterButtonText,
    bestButtonText,
    goodUrl,
    betterUrl,
    bestUrl,
  } = comparisonModule.fields;
  const withBuilderPartnerParams = useBuilderPartnerCheckoutHref();
  const pathname = usePathname();

  const plans = [
    {
      key: "good",
      name: tableHeader?.fields.goodText,
      price: tableHeader?.fields.goodSubtitle,
      url: goodUrl,
      buttonText: goodButtonText,
      textField: "goodText",
      includedField: "goodIncluded",
    },
    {
      key: "better",
      name: tableHeader?.fields.betterText,
      price: tableHeader?.fields.betterSubtitle,
      url: betterUrl,
      buttonText: betterButtonText,
      textField: "betterText",
      includedField: "betterIncluded",
    },
    {
      key: "best",
      name: tableHeader?.fields.bestText,
      price: tableHeader?.fields.bestSubtitle,
      url: bestUrl,
      buttonText: bestButtonText,
      textField: "bestText",
      includedField: "bestIncluded",
    },
  ] as const;

  // On a plan's own page, its column's action is that page's Add to Cart:
  // clicking the real link keeps its checkout URL, analytics and cookie reset.
  // Looked up on click, since the product block can render after this table.
  function addCurrentPlanToCart() {
    const link = Array.from(
      document.querySelectorAll<HTMLAnchorElement>('[id^="pdp-add-to-cart"] a'),
    ).find((a) => a.offsetParent !== null);
    if (link) link.click();
    else window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // The plan whose own page this is: its "Learn more" link points here.
  const currentIndex = plans.findIndex(
    (plan) => pathOf(plan.url) === pathOf(pathname),
  );

  function showIcon(boolean: boolean | undefined) {
    if (boolean) {
      // Inline copy of SuccessIcon with the fill routed through the scheme
      // check token (static SVG assets can't be recolored per scheme).
      return (
        <svg
          width={16}
          height={16}
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-label="Success"
          className="text-scheme-check"
        >
          <path
            d="M0.94043 7.77344C0.94043 3.91797 4.05762 0.773438 7.94043 0.773438C11.7959 0.773438 14.9404 3.91797 14.9404 7.77344C14.9404 11.6562 11.7959 14.7734 7.94043 14.7734C4.05762 14.7734 0.94043 11.6562 0.94043 7.77344ZM11.085 6.57031C11.3857 6.26953 11.3857 5.80469 11.085 5.50391C10.7842 5.20312 10.3193 5.20312 10.0186 5.50391L7.06543 8.45703L5.83496 7.25391C5.53418 6.95312 5.06934 6.95312 4.76855 7.25391C4.46777 7.55469 4.46777 8.01953 4.76855 8.32031L6.51855 10.0703C6.81934 10.3711 7.28418 10.3711 7.58496 10.0703L11.085 6.57031Z"
            fill="currentColor"
          />
        </svg>
      );
    }
    return (
      <span
        role="img"
        aria-label="Not included"
        className="bg-system-grayscale-30 block h-0.5 w-3 rounded-full"
      />
    );
  }

  return (
    <div className="flex w-full">
      <div
        className={cn(
          "mx-auto flex w-full max-w-7xl flex-col items-center",
          isMultiComparisonModule ? "px-0 pb-0 pt-10 md:pt-12" : "px-6 py-10",
        )}
      >
        <div className="text-center">
          <Typography
            as="h2"
            variant="h2"
            color="primary"
            className="hidden md:block"
          >
            {heading}
          </Typography>
          <Typography
            as="h2"
            variant="h3"
            color="primary"
            className="md:hidden"
          >
            {mobileHeading || heading}
          </Typography>
        </div>
        {tabSwitcher}
        <div className="mt-6 w-full">
          <Table className="w-full table-fixed">
            <TableHeader className="[&_tr]:border-b-0">
              <TableRow className="border-0 hover:bg-transparent">
                <TableHead className="w-[34%] p-3 align-bottom md:w-1/4">
                  <Typography as="span" variant="h5" color="primary">
                    {tableHeader?.fields.rowHeading}
                  </Typography>
                </TableHead>
                {plans.map((plan, i) => (
                  <TableHead
                    key={plan.key}
                    className={cn(
                      "w-[22%] px-1 py-3 pt-4 align-bottom md:w-1/4 md:px-3",
                      i === currentIndex &&
                        "bg-scheme-accent/15 border-scheme-highlight-border rounded-t-xl border-x border-t",
                    )}
                  >
                    <div className="flex flex-col items-center gap-1 text-center">
                      <div className="flex flex-col items-center gap-1 md:flex-row md:items-center md:gap-2">
                        <Typography
                          as="span"
                          variant="title-sm"
                          color="primary"
                          className="md:hidden"
                        >
                          {plan.name}
                        </Typography>
                        <Typography
                          as="span"
                          variant="h5"
                          color="primary"
                          className="hidden md:inline"
                        >
                          {plan.name}
                        </Typography>
                        {plan.price && (
                          <span className="border-scheme-border rounded-full border px-3 py-0.5">
                            <Typography
                              as="span"
                              variant="body-sm"
                              color="primary"
                            >
                              {plan.price}
                            </Typography>
                          </span>
                        )}
                      </div>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableRows?.map((row) => (
                <TableRow
                  key={row?.sys.id}
                  className="odd:bg-system-grayscale-00/30 odd:hover:bg-system-grayscale-00/30 border-0 hover:bg-transparent"
                >
                  <TableCell className="w-[34%] p-3 pr-0 md:w-1/4 md:pr-8">
                    <Typography variant="body" color="primary">
                      {row?.fields.rowHeading}
                    </Typography>
                  </TableCell>
                  {plans.map((plan, i) => (
                    <TableCell
                      key={plan.key}
                      className={cn(
                        "w-[22%] px-1 py-3 md:w-1/4 md:px-3",
                        i === currentIndex &&
                          "bg-scheme-accent/15 border-scheme-highlight-border border-x",
                      )}
                    >
                      <div className="flex flex-row items-center justify-center text-center">
                        {row?.fields.type === "text" ? (
                          <Typography as="span" variant="body" color="primary">
                            {row?.fields[plan.textField]}
                          </Typography>
                        ) : (
                          showIcon(row?.fields[plan.includedField])
                        )}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow className="border-0 hover:bg-transparent">
                <TableCell className="w-1/4 p-3" />
                {plans.map((plan, i) => (
                  <TableCell
                    key={plan.key}
                    className={cn(
                      "w-1/4 p-1 text-center md:p-3",
                      i === currentIndex &&
                        "bg-scheme-accent/15 border-scheme-highlight-border rounded-b-xl border-x border-b",
                    )}
                  >
                    {i === currentIndex ? (
                      <Button
                        className="w-full px-2 py-4 text-xs leading-3 md:!px-2 md:text-base"
                        size="sm"
                        onClick={addCurrentPlanToCart}
                      >
                        Add to cart
                      </Button>
                    ) : (
                      <Button
                        className="w-full px-2 py-4 text-xs leading-3 md:!px-2 md:text-base"
                        size="sm"
                        variant={currentIndex === -1 ? "default" : "outline"}
                        asChild
                      >
                        <a href={withBuilderPartnerParams(plan.url)}>
                          {plan.buttonText}
                        </a>
                      </Button>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
